import BoldText from '@/components/BoldText';
import RegularText from '@/components/RegularText';
import { METHOD_OPTIONS } from '@/constants/enum';
import { handleSaveWidgetTextDetail, widgetTextDetailSelector } from '@/redux/slice/widgetText.slice';
import { Box, Button, Card, InlineGrid, InlineStack, TextField } from '@shopify/polaris';
import { DeliveryIcon, GlobeAsiaIcon, StoreManagedIcon } from '@shopify/polaris-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RevertDefaultModal } from '../../../Modal/RevertDefaultModal';
import { StyledTab } from '../../../styled';
import { DeliveryText } from './component/DeliveryText';
import { PickupText } from './component/PickupText';
import { ShippingText } from './component/ShippingText';
import { useUpdateRevertTextMutation } from '@/redux/query';
import { config } from '@/config';
import { openToast } from '@/helpers';

const CartPage = () => {
  const { t } = useTranslation('translation');
  const dispatch = useDispatch();

  const widgetTextDetail = useSelector(widgetTextDetailSelector);

  const [selectedTab, setSelectedTab] = useState(METHOD_OPTIONS.PICKUP);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const [updateRevertText, { isLoading: isLoadingRevertText }] = useUpdateRevertTextMutation();

  const revertDefault = () => {
    updateRevertText({
      shop: config.shop,
      code: widgetTextDetail.code!,
      calendarLocale: widgetTextDetail.calendarLocale!,
    })
      .unwrap()
      .then((res) => {
        const processedData = {
          additionalMessage: res.data.additionalMessage,
          headerTitle: res.data.headerTitle,
          texts: res.data.texts.map((text) => {
            const fallback = widgetTextDetail.texts?.find((t) => t.type === text.type);
            return {
              ...text,
              orderStatusMessage: text.orderStatusMessage ?? fallback?.orderStatusMessage,
            };
          }),
        };

        dispatch(handleSaveWidgetTextDetail(processedData));
        setIsOpenModal(false);
        openToast(dispatch, 'Reverted to default successfully');
      });
  };

  return (
    <>
      <Box paddingBlock="400">
        <Card padding="400">
          <InlineStack align="space-between">
            <BoldText>{t('cartPage.title')}</BoldText>
            <Button variant="plain" onClick={() => setIsOpenModal(true)}>
              {t('revert.btn')}
            </Button>
          </InlineStack>
          <RegularText tone="subdued">{t('cartPage.description')}</RegularText>

          <Box paddingBlock="200">
            <TextField
              label={t('cartPage.widgetHeading')}
              autoComplete="off"
              value={widgetTextDetail.headerTitle}
              onChange={(value: string) => dispatch(handleSaveWidgetTextDetail({ headerTitle: value }))}
            />
          </Box>
          <Box paddingBlock="200">
            <TextField
              label={t('cartPage.widgetAdditionalMessage')}
              autoComplete="off"
              helpText={t('cartPage.widgetAdditionalHelpText')}
              value={widgetTextDetail.additionalMessage}
              onChange={(value: string) => dispatch(handleSaveWidgetTextDetail({ additionalMessage: value }))}
            />
          </Box>

          <Box paddingBlock="200">
            <StyledTab>
              <InlineGrid columns={3} gap={'100'}>
                <Button
                  variant="tertiary"
                  pressed={selectedTab === METHOD_OPTIONS.PICKUP}
                  icon={StoreManagedIcon}
                  onClick={() => setSelectedTab(METHOD_OPTIONS.PICKUP)}
                >
                  Store pickup
                </Button>
                <Button
                  variant="tertiary"
                  pressed={selectedTab === METHOD_OPTIONS.DELIVERY}
                  icon={DeliveryIcon}
                  onClick={() => setSelectedTab(METHOD_OPTIONS.DELIVERY)}
                >
                  Local delivery
                </Button>
                <Button
                  variant="tertiary"
                  pressed={selectedTab === METHOD_OPTIONS.SHIPPING}
                  icon={GlobeAsiaIcon}
                  onClick={() => setSelectedTab(METHOD_OPTIONS.SHIPPING)}
                >
                  Shipping
                </Button>
              </InlineGrid>
            </StyledTab>
          </Box>
          {selectedTab === METHOD_OPTIONS.PICKUP && <PickupText />}
          {selectedTab === METHOD_OPTIONS.DELIVERY && <DeliveryText />}
          {selectedTab === METHOD_OPTIONS.SHIPPING && <ShippingText />}
        </Card>
      </Box>
      <RevertDefaultModal
        open={isOpenModal}
        onCancel={() => setIsOpenModal(false)}
        onSubmit={revertDefault}
        loading={isLoadingRevertText}
      />
    </>
  );
};

export default CartPage;
